import { onMounted, onUnmounted } from 'vue'
import { useNotificationCenterStore } from '../stores/notification-center.store'

// The backend publishes new notifications every 30 seconds (Hubs/NotificationService), so
// checking any more often would find nothing new.
export const NOTIFICATIONS_POLL_MS = 30_000

/**
 * Keeps the bell current. Vue 2 is told about new notifications over SignalR (/notificationHub);
 * this reads the stored ones back instead: the first load on mount, then every 30 seconds while
 * the tab is visible, and at once when the user comes back to the tab. A hidden tab makes no
 * requests. Mount it once, in the component that shows the bell.
 */
export function useNotificationCenterPolling() {
	const store = useNotificationCenterStore()
	let timer: ReturnType<typeof setTimeout> | null = null
	let active = false

	function stop(): void {
		if (timer) {
			clearTimeout(timer)
			timer = null
		}
	}

	function scheduleNext(): void {
		stop()
		if (!active || document.hidden) return
		timer = setTimeout(() => void tick(), NOTIFICATIONS_POLL_MS)
	}

	async function tick(): Promise<void> {
		await store.refresh()
		scheduleNext()
	}

	function onVisibilityChange(): void {
		if (document.hidden) stop()
		else void tick()
	}

	onMounted(() => {
		active = true
		document.addEventListener('visibilitychange', onVisibilityChange)
		void tick()
	})

	onUnmounted(() => {
		active = false
		stop()
		document.removeEventListener('visibilitychange', onVisibilityChange)
	})
}
